import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { httpClient } from '../../../common/httpclient';
import { formatCurrency } from '../../../common/formatters';
import { formatBRMoney, parseBRMoney, PAYMENT_METHODS } from '../shared';

// Estado + regras das ações de caixa (abrir, sangria, fechar), compartilhado
// entre os modais/drawer do desktop (`OpenCashModal`, `SangriaModal`,
// `CloseCashDrawer`) e as páginas do shell mobile (`Open.js`, `Sangria.js`,
// `Close.js`). Só concentra a lógica que já existia nos modais — mesmas
// chamadas, payloads e validações; a casca visual fica em cada tela.

export const ISO_FORMAT = 'YYYY-MM-DD';

// `POST /cashregister/{id}/open`
export function useOpenCash({ open, cashRegister, onSuccess, onError }) {
    const [amount, setAmount] = useState('');
    const [obs, setObs] = useState('');
    const [amountError, setAmountError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount('');
            setObs('');
            setAmountError('');
        }
    }, [open]);

    const changeAmount = (value) => {
        setAmount(formatCurrency(value));
        if (amountError) setAmountError('');
    };

    const confirm = async () => {
        if (amount === '' || parseFloat(amount.replace(/\./g, '').replace(',', '.')) < 0) {
            setAmountError('Informe o valor inicial em espécie para abrir o caixa.');
            return;
        }
        setAmountError('');
        setSaving(true);
        try {
            // `httpClient` (axios `validateStatus: () => true`, ver
            // `common/httpclient.js`) nunca rejeita a Promise por status
            // HTTP — só por erro de rede. Por isso o status é checado à mão.
            const response = await httpClient.post(`/cashregister/${cashRegister.id}/open`, {
                cash_register_id: cashRegister.id,
                amount_money: amount,
                obs,
                start_operation: true,
            });
            if (response.status === 200) {
                onSuccess?.({ amount, obs });
            } else {
                onError?.(response.data);
            }
        } catch (error) {
            console.error('Erro ao abrir caixa:', error);
            onError?.(error.response?.data || error.message || 'Erro ao abrir o caixa.');
        } finally {
            setSaving(false);
        }
    };

    return { amount, obs, setObs, amountError, saving, changeAmount, confirm };
}

// Texto de erro vindo da API (`http.Error` devolve string com quebra de linha no fim; JSON traz message/error).
const errorText = (data) => {
    const text = typeof data === 'string' ? data : (data?.message || data?.error || '');
    return String(text).trim() || 'Não foi possível registrar a sangria.';
};

// `POST /cashregister/bleeding`
export function useSangria({ open, cashRegister, onSuccess, onError }) {
    const [amount, setAmount] = useState('');
    const [reason, setReason] = useState('');
    const [amountError, setAmountError] = useState('');
    const [reasonError, setReasonError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount('');
            setReason('');
            setAmountError('');
            setReasonError('');
        }
    }, [open]);

    const changeAmount = (value) => {
        setAmount(formatCurrency(value));
        if (amountError) setAmountError('');
    };
    const changeReason = (value) => {
        setReason(value);
        if (reasonError) setReasonError('');
    };

    const confirm = async () => {
        const numeric = parseFloat(String(amount).replace(/\./g, '').replace(',', '.'));
        let hasError = false;
        if (!amount || Number.isNaN(numeric) || numeric <= 0) {
            setAmountError('Informe o valor da sangria.');
            hasError = true;
        } else {
            setAmountError('');
        }
        if (!reason.trim()) {
            setReasonError('Informe o motivo da sangria.');
            hasError = true;
        } else {
            setReasonError('');
        }
        if (hasError) return;

        setSaving(true);
        try {
            const response = await httpClient.post('/cashregister/bleeding', {
                cash_register_id: cashRegister.id,
                value: amount,
                reason: reason.trim(),
            });
            if (response.status === 200) {
                onSuccess?.({ amount, reason: reason.trim() });
            } else {
                // Recusa de regra (400, ex.: valor maior que o saldo do caixa): mostra no próprio campo,
                // dentro do modal, além do aviso (toast) do chamador.
                if (response.status === 400) setAmountError(errorText(response.data));
                onError?.(response.data);
            }
        } catch (error) {
            console.error('Erro ao registrar sangria:', error);
            onError?.(error.response?.data || error.message || 'Erro ao registrar a sangria.');
        } finally {
            setSaving(false);
        }
    };

    return { amount, reason, amountError, reasonError, saving, changeAmount, changeReason, confirm };
}

// `POST /cashregister/{id}/close` (+ `GET /cashregister/{id}/resume?startDate=&endDate=`
// quando o período diverge do padrão). Ver o comentário de `CloseCashDrawer`.
export function useCloseCash({ open, cashRegister, sessionStart, expected, onSuccess, onError }) {
    const [counted, setCounted] = useState({});
    const [observation, setObservation] = useState('');
    const [saving, setSaving] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    // Esperado de Pix/Cartão do período escolhido; `null` = usa `expected`
    // do chamador (período padrão: abertura da sessão até hoje).
    const [periodExpected, setPeriodExpected] = useState(null);
    const [loadingPeriod, setLoadingPeriod] = useState(false);
    const [periodError, setPeriodError] = useState('');

    const defaultStartIso = sessionStart?.dateIso || dayjs().format(ISO_FORMAT);
    const defaultEndIso = dayjs().format(ISO_FORMAT);

    const startIso = startDate?.isValid() ? startDate.format(ISO_FORMAT) : null;
    const endIso = endDate?.isValid() ? endDate.format(ISO_FORMAT) : null;
    const rangeError = !startIso || !endIso
        ? 'Informe as duas datas.'
        : startIso > endIso
            ? 'A data inicial não pode ser maior que a final.'
            : '';

    const effectiveExpected = useMemo(() => ({
        dinheiro: expected?.dinheiro || 0,
        pix: periodExpected ? periodExpected.pix : (expected?.pix || 0),
        cartao: periodExpected ? periodExpected.cartao : (expected?.cartao || 0),
    }), [expected, periodExpected]);

    const prefillCounted = (source) => {
        const initial = {};
        PAYMENT_METHODS.forEach((m) => {
            initial[m.key] = formatBRMoney(source?.[m.key] || 0);
        });
        return initial;
    };

    useEffect(() => {
        if (open) {
            setCounted(prefillCounted(expected));
            setObservation('');
            setStartDate(dayjs(defaultStartIso));
            setEndDate(dayjs(defaultEndIso));
            setPeriodExpected(null);
            setPeriodError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cashRegister?.id]);

    // Rebusca o esperado quando o período diverge do padrão.
    useEffect(() => {
        if (!open || !cashRegister?.id || rangeError) return undefined;
        if (startIso === defaultStartIso && endIso === defaultEndIso) {
            setPeriodExpected(null);
            setPeriodError('');
            setCounted((prev) => ({ ...prev, pix: formatBRMoney(expected?.pix || 0), cartao: formatBRMoney(expected?.cartao || 0) }));
            return undefined;
        }
        let cancelled = false;
        setLoadingPeriod(true);
        setPeriodError('');
        httpClient
            .get(`/cashregister/${cashRegister.id}/resume?startDate=${startIso}&endDate=${endIso}`)
            .then((response) => {
                if (cancelled) return;
                if (response.status !== 200) {
                    setPeriodExpected(null);
                    setPeriodError('Não foi possível carregar os valores esperados do período.');
                    return;
                }
                const next = {
                    pix: parseBRMoney(response.data?.total_pix),
                    cartao: parseBRMoney(response.data?.total_gross_machine),
                };
                setPeriodExpected(next);
                setCounted((prev) => ({ ...prev, pix: formatBRMoney(next.pix), cartao: formatBRMoney(next.cartao) }));
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao carregar esperado do período:', error);
                setPeriodExpected(null);
                setPeriodError('Não foi possível carregar os valores esperados do período.');
            })
            .finally(() => {
                if (!cancelled) setLoadingPeriod(false);
            });
        return () => {
            cancelled = true;
            setLoadingPeriod(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, cashRegister?.id, startIso, endIso]);

    const totals = useMemo(() => {
        let totalExp = 0;
        let totalCount = 0;
        PAYMENT_METHODS.forEach((m) => {
            totalExp += effectiveExpected[m.key] || 0;
            totalCount += parseBRMoney(counted[m.key]);
        });
        return { totalExp, totalCount, diff: totalCount - totalExp };
    }, [counted, effectiveExpected]);

    const changeCounted = (key, value) => setCounted((prev) => ({ ...prev, [key]: formatCurrency(value) }));

    const confirm = async () => {
        setSaving(true);
        try {
            // O backend rejeita com 400 ("O total informado não é igual ao
            // total do caixa") quando o total contado excede o total de
            // vendas do período — por isso o status é checado à mão.
            const response = await httpClient.post(`/cashregister/${cashRegister.id}/close`, {
                total_amount_cash: counted.dinheiro || '0,00',
                total_amount_pix: counted.pix || '0,00',
                total_amount_credit: counted.cartao || '0,00',
                total_amount_debit: '0,00',
                start_date: startIso,
                end_date: endIso,
            });
            if (response.status === 200) {
                onSuccess?.({ totalCounted: totals.totalCount, diff: totals.diff, observation: observation.trim() });
            } else {
                onError?.(response.data);
            }
        } catch (error) {
            console.error('Erro ao fechar caixa:', error);
            onError?.(error.response?.data || error.message || 'Erro ao fechar o caixa.');
        } finally {
            setSaving(false);
        }
    };

    return {
        counted,
        changeCounted,
        observation,
        setObservation,
        saving,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        loadingPeriod,
        periodError,
        rangeError,
        effectiveExpected,
        totals,
        confirm,
    };
}
